import { t } from '../i18n';
import { useEffect, useState, type FC, Suspense } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  apiErrorMessage,
  fetchCourseProgress,
  fetchStudentByTelegramId,
  spendCoinsApi,
  type Lesson,
} from '../api/client';
import { useAppStore } from '../store/useAppStore';
import { Header } from '../components/Header';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Hourglass,
  Lock,
  Play,
  ShieldAlert,
  Sparkles,
} from 'lucide-react';
import WebApp from '@twa-dev/sdk';
import { motion, AnimatePresence } from 'framer-motion';
import { lazyWithRetry } from '../lib/lazyWithRetry';
import { PageFallback } from '../components/PageFallback';
import { useConfirm } from '../components/ConfirmProvider';
import { usePresence } from '../hooks/usePresence';

// Ustoz rejimi o'quvchilarning asosiy fayliga tushmasin.
const TeacherViewPage = lazyWithRetry(() =>
  import('./TeacherViewPage').then((m) => m.TeacherViewPage),
);

/** Darsning ko'rsatiladigan holati — bitta joyda hisoblanadi. */
type LessonState = 'done' | 'current' | 'locked' | 'missed';

function lessonState(lesson: Lesson): LessonState {
  if (lesson.isCompleted) return 'done';
  if (lesson.deadlineMissed) return 'missed';
  if (lesson.isUnlocked) return 'current';
  return 'locked';
}

/** Muddat qolgan kunini o'quvchiga tushunarli qilib yozadi. */
function deadlineLabel(deadlineAt?: string | null): string | null {
  if (!deadlineAt) return null;
  const days = Math.ceil(
    (new Date(deadlineAt).getTime() - Date.now()) / 86400000,
  );
  if (days < 0) return t('Muddati oʻtgan');
  if (days === 0) return 'Bugun tugaydi';
  if (days === 1) return 'Ertaga tugaydi';
  return `${days} kun qoldi`;
}

const Shell: FC<{ children: React.ReactNode; title?: string }> = ({
  children,
  title,
}) => (
  <>
    <Header courseTitle={title} />
    <main className="page">{children}</main>
  </>
);

export const CourseListPage: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    studentId,
    courseId,
    telegramId,
    setIds,
    setStudentName,
    setSelectedLesson,
    role,
    setRole,
    setGamification,
    coins,
    xp,
    streak,
    enrollments,
    clearCourse,
  } = useAppStore();

  const {
    data: tgData,
    isLoading: tgLoading,
    isError: tgError,
    isFetching: tgFetching,
    refetch: tgRefetch,
  } = useQuery({
    queryKey: ['student-by-tg', telegramId],
    queryFn: () => fetchStudentByTelegramId(telegramId!),
    enabled: !!telegramId,
    retry: false,
  });

  const {
    data: course,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['course-progress', studentId, courseId],
    queryFn: () => fetchCourseProgress(studentId!, courseId!),
    enabled: !!studentId && !!courseId,
  });

  const confirm = useConfirm();

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  // Hook erta qaytishlardan OLDIN — React #310 dan keyingi qoida.
  const online = usePresence(courseId, null);

  /*
   * HOOK BARCHA ERTA QAYTISHLARDAN OLDIN TURISHI SHART.
   *
   * Bu effekt avval `coinConfig` e'lon qilingan joyga — ya'ni yettita
   * `if (...) return <...>` dan KEYINGA qo'yilgan edi. Yuklanish paytida
   * komponent o'sha qaytishlarning birida to'xtaydi va bu hook umuman
   * chaqirilmaydi; ma'lumot kelgach esa chaqiriladi. Hooklar soni
   * renderlar orasida o'zgarib, React #310 bilan butun ilova qulardi.
   */
  useEffect(() => {
    const enabled = !!(course as any)?.course?.coins?.enabled;
    useAppStore.getState().setCoinsEnabled(enabled);
  }, [course]);

  useEffect(() => {
    if (tgData) {
      if (tgData.isTeacher || tgData.role === 'teacher') {
        setRole('teacher');
        if (tgData.teacher?.fullName) setStudentName(tgData.teacher.fullName);
      } else if (tgData.student) {
        setRole('student');
        const s = tgData.student;
        const cId = s.group?.course?.id;
        if (s.id && cId) {
          setIds(s.id, cId, s.group?.id);
          setStudentName(s.fullName);
          setGamification(s.coins || 0, s.xp || 0, s.streak || 0);
          if (s.group?.teacher) {
            useAppStore
              .getState()
              .setCenterName(
                s.group.teacher.centerName || s.group.teacher.fullName,
              );
          }
        }
      }
    } else if (tgError) {
      setRole('student');
    }
  }, [tgData, tgError, setIds, setStudentName, setRole, setGamification]);

  // ---------------------------------------------------------------- holatlar

  /*
   * Bu uchta shart ilgari BITTA `if` da edi:
   *
   *   if (tgLoading || !telegramId || !tgData) return <skeleton/>
   *
   * Ya'ni Telegram konteksti bo'lmasa yoki so'rov yiqilsa, ilova ABADIY
   * miltillovchi skeletonda qolardi — o'quvchi na sababni, na nima qilishni
   * biladi. Bu faqat brauzerda ochilganda emas, real yo'llarda ham yuz
   * beradi: initData muddati tugagan (`TG_INITDATA_MAX_AGE_SEC`), tarmoq
   * uzilgan, o'quvchi o'chirilgan. Endi har biri o'z yakuniy holatiga ega.
   */

  // 1. Haqiqatan yuklanmoqda
  if (telegramId && tgLoading) {
    return (
      <Shell>
        <div
          className="skeleton skeleton-line"
          style={{ width: '60%', height: 22 }}
        />
        <div className="skeleton skeleton-line" style={{ width: '35%' }} />
        <div style={{ height: 'var(--space-6)' }} />
        <div className="skeleton skeleton-card" style={{ height: 92 }} />
        <div className="skeleton skeleton-card" />
        <div className="skeleton skeleton-card" />
        <div className="skeleton skeleton-card" />
      </Shell>
    );
  }

  // 2. Telegram konteksti yo'q — ilova oddiy brauzerda ochilgan
  if (!telegramId) {
    return (
      <Shell>
        <div className="empty">
          <ShieldAlert size={26} className="muted" aria-hidden="true" />
          <div className="empty-title">{t('Telegram orqali oching')}</div>
          <p className="empty-text">
            {t(
              'Bu ilova Telegram bot ichida ishlaydi. Botga qayting va {command} buyrugʻini yuboring.',
              { command: '/mycourse' },
            )}
          </p>
        </div>
      </Shell>
    );
  }

  // 3. So'rov yiqildi yoki bo'sh javob qaytdi
  if (tgError || !tgData) {
    return (
      <Shell>
        <div className="empty">
          <ShieldAlert size={26} className="muted" aria-hidden="true" />
          <div className="empty-title">{t('Ulanib boʻlmadi')}</div>
          <p className="empty-text">
            {t(
              'Maʼlumotlaringizni olishning iloji boʻlmadi. Internetni tekshirib qayta urinib koʻring.',
            )}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: 'var(--space-4)' }}
            onClick={() => void tgRefetch()}
            disabled={tgFetching}
          >
            {tgFetching ? t('Urinilmoqda…') : t('Qayta urinish')}
          </button>
        </div>
      </Shell>
    );
  }

  if (role === 'teacher' && !tgError)
    return (
      <Suspense fallback={<PageFallback />}>
        <TeacherViewPage />
      </Suspense>
    );

  if (tgData?.isPending) {
    return (
      <Shell>
        <div className="empty">
          <Hourglass size={26} className="muted" aria-hidden="true" />
          <div className="empty-title">{t('Arizangiz koʻrib chiqilmoqda')}</div>
          <p className="empty-text">
            {t(
              'Soʻrovingiz ustozga yuborildi. Tasdiqlangach darslar shu yerda ochiladi.',
            )}
          </p>
        </div>
      </Shell>
    );
  }

  if (tgData?.student?.status === 'BLOCKED') {
    return (
      <Shell>
        <div className="empty">
          <ShieldAlert
            size={26}
            style={{ color: 'var(--danger)' }}
            aria-hidden="true"
          />
          <div className="empty-title">{t('Hisobingiz bloklangan')}</div>
          <p className="empty-text">
            {t(
              'Oʻqish muddati tugagan yoki hisob toʻxtatilgan. Ustozingiz bilan bogʻlaning.',
            )}
          </p>
        </div>
      </Shell>
    );
  }

  if (isLoading || (!studentId && !tgData)) {
    return (
      <Shell>
        <div className="skeleton skeleton-card" style={{ height: 92 }} />
        <div className="skeleton skeleton-card" />
        <div className="skeleton skeleton-card" />
      </Shell>
    );
  }

  if (isError || !course) {
    return (
      <Shell>
        <div className="empty">
          <ShieldAlert size={26} className="muted" aria-hidden="true" />
          <div className="empty-title">{t('Maʼlumotlar topilmadi')}</div>
          <p className="empty-text">
            {t('Botga qayting va {command} buyrugʻi orqali qaytadan kiring.', {
              command: '/mycourse',
            })}
          </p>
        </div>
      </Shell>
    );
  }

  // ------------------------------------------------------------------ hisob

  const allLessons = course.modules.flatMap((m) => m.lessons);
  const total = allLessons.length;
  const done = allLessons.filter((l) => l.isCompleted).length;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  const next = allLessons.find((l) => !l.isCompleted && l.isUnlocked);
  /* Davom etish kartasi uchun — dars qaysi modulda turgani. */
  const nextModule = next
    ? course.modules.find((m) => m.lessons.some((l) => l.id === next.id))
    : null;

  /*
   * Tanga evaziga to'siqni ochish.
   *
   * Taklif AYNAN o'quvchi devorga urilgan paytda chiqadi — alohida
   * "do'kon" ekrani emas. Modul o'chiq yoki narx 0 bo'lsa hech narsa
   * ko'rsatilmaydi va eski xatti-harakat saqlanadi.
   */
  const coinConfig = (course as any)?.course?.coins;

  const buy = async (
    lesson: Lesson,
    moduleTitle: string,
    action: 'REOPEN_LESSON' | 'SKIP_LESSON',
    price: number,
  ) => {
    const body =
      action === 'REOPEN_LESSON'
        ? t(
            '{price} tanga evaziga muddati oʻtgan darsni ochish. Balansingiz: {coins}.',
            { price, coins },
          )
        : t(
            '{price} tanga evaziga darsni navbatsiz ochish. Balansingiz: {coins}.',
            { price, coins },
          );

    if (coins < price) {
      toast.error(
        t('Tanga yetarli emas: {price} kerak, sizda {coins}.', {
          price,
          coins,
        }),
      );
      return;
    }
    const ok = await confirm({
      title: t('Tanga sarflansinmi?'),
      body,
      confirmText: t('Ha, ochish'),
    });
    if (!ok) return;

    try {
      const r = await spendCoinsApi(lesson.id, action);
      const s = useAppStore.getState();
      s.setGamification(r.coins, s.xp, s.streak);
      toast.success(t('Ochildi. Qolgan tanga: {coins}', { coins: r.coins }));
      await queryClient.invalidateQueries({ queryKey: ['course-progress'] });
      setSelectedLesson({ ...lesson, moduleTitle } as any);
      navigate(`/lesson/${lesson.id}`);
    } catch (e: any) {
      toast.error(apiErrorMessage(e, t('Ochib boʻlmadi.')));
    }
  };

  const open = (lesson: Lesson, moduleTitle: string) => {
    const state = lessonState(lesson);

    if (state === 'locked') {
      try {
        WebApp.HapticFeedback.notificationOccurred('warning');
      } catch {
        /* Telegramdan tashqarida */
      }
      if (coinConfig?.enabled && coinConfig.skipLesson > 0) {
        void buy(lesson, moduleTitle, 'SKIP_LESSON', coinConfig.skipLesson);
        return;
      }
      toast(t('Avvalgi darsni yakunlang.'), { icon: '🔒' });
      return;
    }

    if (state === 'missed') {
      if (coinConfig?.enabled && coinConfig.reopenLesson > 0) {
        void buy(lesson, moduleTitle, 'REOPEN_LESSON', coinConfig.reopenLesson);
        return;
      }
      toast.error(
        t('Bu darsning muddati oʻtgan. Ustozingizga murojaat qiling.'),
      );
      return;
    }

    try {
      WebApp.HapticFeedback.impactOccurred('medium');
    } catch {
      /* Telegramdan tashqarida */
    }
    setSelectedLesson({ ...lesson, moduleTitle } as any);
    navigate(`/lesson/${lesson.id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      {/* Server `{ course: {...}, modules }` qaytaradi — `course.title`
          YASSI EMAS. Shu sababli kurs nomi hech qachon ko'rinmagan va
          sarlavha ustoz nomiga (centerName) tushib qolgan. */}
      <Header courseTitle={(course as any).course?.title || course.title} />

      <main className="page">
        {/*
          Kursni almashtirish — faqat bir nechta kursda o'qiyotgan
          o'quvchida ko'rinadi. Bitta kursda bu tugma keraksiz va
          chalg'ituvchi bo'lardi ("qayerga qaytaman?").
        */}
        {enrollments.length > 1 && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            style={{ marginBottom: 'var(--space-3)' }}
            onClick={clearCourse}
          >
            <ChevronLeft size={14} aria-hidden="true" />
            {t('Kurslarim')}
          </button>
        )}

        <section style={{ marginBottom: 'var(--space-6)' }}>
          <h1>{course.title}</h1>
          {course.description && (
            <p className="secondary text-sm" style={{ marginTop: 4 }}>
              {course.description}
            </p>
          )}

          {/*
            SAYT KOʻRINISHI — faqat keng ekranda (`.hero`).

            Mini App'da sahifa tor ustun va darhol darslar roʻyxatiga
            oʻtadi. Kompyuterda esa ekranning yarmi boʻsh qolardi va
            sahifa "tugallanmagan" koʻrinardi. Bu blok boʻshliqni
            BEZAK bilan emas, MAʼLUMOT bilan toʻldiradi: qayerdan
            davom etish va qanday natijalar.

            Telefonda u koʻrinmaydi, yaʼni Mini App oʻzgarishsiz
            qoladi — quyidagi progress chizigʻi esa aksincha, faqat
            telefonda.
          */}
          <section className="hero">
            {next ? (
              <div className="continue-card card">
                <span className="stat-label">{t('Davom etish')}</span>
                <h2 style={{ margin: '6px 0 4px' }}>{next.title}</h2>
                <p
                  className="secondary text-sm"
                  style={{ margin: '0 0 var(--space-4)' }}
                >
                  {nextModule?.title}
                  {next.tests?.length
                    ? ` · ${t('{count} savol', { count: next.tests.length })}`
                    : ''}
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => open(next, nextModule?.title || '')}
                >
                  {t('Darsni ochish')}
                </button>
              </div>
            ) : (
              <div className="continue-card card">
                <span className="stat-label">{t('Kurs progressi')}</span>
                <h2 style={{ margin: '6px 0 4px' }}>
                  {t('Hamma dars yakunlandi')}
                </h2>
                <p className="secondary text-sm" style={{ margin: 0 }}>
                  {t('Sertifikatingiz profilda.')}
                </p>
              </div>
            )}

            <div className="tiles">
              <div className="card-quiet stat">
                <span className="stat-value">{percent}%</span>
                <span className="stat-label">{t('Oʻzlashtirish')}</span>
              </div>
              <div className="card-quiet stat">
                <span className="stat-value">{xp}</span>
                <span className="stat-label">{t('XP')}</span>
              </div>
              <div className="card-quiet stat">
                <span className="stat-value">{coins}</span>
                <span className="stat-label">{t('Tanga')}</span>
              </div>
              <div className="card-quiet stat">
                <span className="stat-value">{streak}</span>
                <span className="stat-label">{t('Kun')}</span>
              </div>
            </div>
          </section>

          <div className="phone-only" style={{ marginTop: 'var(--space-4)' }}>
            <div className="spread" style={{ marginBottom: 6 }}>
              <span className="stat-label">{t('Kurs progressi')}</span>
              <span
                className="display tabular"
                style={{ fontSize: 'var(--text-lg)', fontWeight: 600 }}
              >
                {percent}%
              </span>
            </div>
            <div
              className="meter"
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={t('Kurs progressi')}
            >
              <div
                className={`meter-fill${percent === 100 ? ' is-complete' : ''}`}
                style={{ width: `${percent}%` }}
              />
            </div>
            <p className="muted text-xs" style={{ marginTop: 6 }}>
              {t('{done} / {total} dars yakunlangan', { done, total })}
            </p>

            {/*
              HAR DOIM ko'rsatiladi.
              Ilgari faqat boshqa odam bo'lgandagina chiqardi — natijada
              yolg'iz o'quvchi (va sinab ko'rayotgan ustoz) bu imkoniyat
              borligini umuman bilmasdi. Endi yolg'iz bo'lsa ham ko'rinadi,
              faqat matn boshqacha.
            */}
            {
              <p
                className="text-xs"
                style={{
                  marginTop: 6,
                  color: 'var(--ok)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    background: 'var(--ok)',
                    flex: 'none',
                  }}
                />
                {online.course > 0 ? (
                  <>
                    {t('Shu kursda hozir: siz va yana {count} kishi', {
                      count: online.course,
                    })}
                  </>
                ) : (
                  <>{t('Shu kursda hozir: faqat siz')}</>
                )}
              </p>
            }
          </div>
        </section>

        {/* Kompyuterda bu karta `.hero` ichida allaqachon bor. */}
        {next && percent < 100 && (
          <section
            className="card ruled ruled-accent phone-only"
            style={{ marginBottom: 'var(--space-6)' }}
          >
            <span className="chip chip-accent">{t('Davom etish')}</span>
            <h2 style={{ margin: '10px 0 4px', fontSize: 'var(--text-lg)' }}>
              {next.title}
            </h2>
            <p
              className="muted text-sm"
              style={{ marginBottom: 'var(--space-4)' }}
            >
              {t('{count} ta dars qoldi', { count: total - done })}
            </p>
            <button
              type="button"
              className="btn btn-primary btn-block"
              onClick={() => {
                const mod = course.modules.find((m) =>
                  m.lessons.some((l) => l.id === next.id),
                );
                open(next, mod?.title || 'Modul');
              }}
            >
              <Play size={17} aria-hidden="true" />
              {t('Darsni ochish')}
            </button>
          </section>
        )}

        <section>
          {course.modules.length === 0 && (
            <div className="empty">
              <div className="empty-title">
                {t('Darslar hali qoʻshilmagan')}
              </div>
              <p className="empty-text">
                {t('Ustozingiz dars qoʻshgach, ular shu yerda paydo boʻladi.')}
              </p>
            </div>
          )}

          {course.modules.map((mod, mIdx) => {
            const isCollapsed = collapsed[mod.id] || false;
            const modDone = mod.lessons.filter((l) => l.isCompleted).length;

            return (
              <div key={mod.id} style={{ marginBottom: 'var(--space-6)' }}>
                <div
                  className="spread"
                  style={{ marginBottom: 'var(--space-3)' }}
                >
                  <button
                    type="button"
                    className="row"
                    onClick={() =>
                      setCollapsed((p) => ({ ...p, [mod.id]: !p[mod.id] }))
                    }
                    aria-expanded={!isCollapsed}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      gap: 'var(--space-2)',
                      textAlign: 'left',
                    }}
                  >
                    {isCollapsed ? (
                      <ChevronRight
                        size={17}
                        className="muted"
                        aria-hidden="true"
                      />
                    ) : (
                      <ChevronDown
                        size={17}
                        className="muted"
                        aria-hidden="true"
                      />
                    )}
                    <span style={{ minWidth: 0 }}>
                      <span
                        className="display"
                        style={{
                          fontSize: 'var(--text-md)',
                          fontWeight: 600,
                          display: 'block',
                        }}
                      >
                        {mIdx + 1}. {mod.title}
                      </span>
                      <span className="muted text-xs">
                        {modDone} / {mod.lessons.length}
                      </span>
                    </span>
                  </button>

                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() =>
                      navigate(`/ai-review/${mod.id}`, {
                        state: { moduleTitle: mod.title },
                      })
                    }
                  >
                    <Sparkles size={14} aria-hidden="true" />
                    {t('Takrorlash')}
                  </button>
                </div>

                <AnimatePresence initial={false}>
                  {!isCollapsed && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div className="stack lesson-grid">
                        {mod.lessons.map((lesson, lIdx) => {
                          const state = lessonState(lesson);
                          const deadline =
                            state === 'current'
                              ? deadlineLabel(lesson.deadlineAt)
                              : null;
                          const ruleClass =
                            state === 'done'
                              ? 'ruled ruled-success'
                              : state === 'current'
                                ? 'ruled ruled-accent'
                                : 'ruled';

                          return (
                            <div key={lesson.id} className={ruleClass}>
                              <button
                                type="button"
                                className={`lesson-row${
                                  state === 'done'
                                    ? ' is-done'
                                    : state === 'current'
                                      ? ' is-current'
                                      : ''
                                }`}
                                onClick={() => open(lesson, mod.title)}
                                disabled={state === 'locked'}
                              >
                                <span className="lesson-num" aria-hidden="true">
                                  {mIdx + 1}.{lIdx + 1}
                                </span>

                                <span style={{ flex: 1, minWidth: 0 }}>
                                  <span className="lesson-title">
                                    {lesson.title}
                                  </span>
                                  <span className="lesson-meta">
                                    {state === 'done' && (
                                      <>
                                        {t('Yakunlandi · {score} ball', {
                                          score: lesson.score ?? 100,
                                        })}
                                      </>
                                    )}
                                    {state === 'missed' && t('Muddati oʻtgan')}
                                    {state === 'locked' &&
                                      t('Avvalgi darsni yakunlang')}
                                    {state === 'current' && (
                                      <>
                                        {lesson.tests?.length
                                          ? t('{count} ta savol', {
                                              count: lesson.tests.length,
                                            })
                                          : t('Dars materiali')}
                                        {deadline && (
                                          <>
                                            {' · '}
                                            <Clock
                                              size={11}
                                              aria-hidden="true"
                                            />
                                            {deadline}
                                          </>
                                        )}
                                      </>
                                    )}
                                  </span>
                                </span>

                                {state === 'done' && (
                                  <Check
                                    size={18}
                                    style={{
                                      color: 'var(--success)',
                                      flex: 'none',
                                    }}
                                    aria-hidden="true"
                                  />
                                )}
                                {state === 'current' && (
                                  <ChevronRight
                                    size={18}
                                    style={{
                                      color: 'var(--accent)',
                                      flex: 'none',
                                    }}
                                    aria-hidden="true"
                                  />
                                )}
                                {state === 'locked' && (
                                  <Lock
                                    size={15}
                                    className="muted"
                                    aria-hidden="true"
                                  />
                                )}
                                {state === 'missed' && (
                                  <span className="chip chip-danger">
                                    {t('Yopiq')}
                                  </span>
                                )}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </section>
      </main>
    </motion.div>
  );
};
